import { Component, Input, OnInit } from '@angular/core';

import moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';

import { SystemNotificationsTypeEnum } from '../../enums/system-notifications-type.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MessageIconTypesEnum } from '../../../../Common/Misc/components/Message/message.component';
import { DeliveryRangeModel } from '../../../../Common/Forms/models/delivery-range.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { SystemNotificationModel } from '../../models/system-notification.model';
import { ResizeDirectionEnum } from '../../../../Common/Forms/components/TextareaInput/textarea-input.component';
import { SystemNotificationService } from '../../services/system-notification.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

@Component({
    selector: 'agpr-systems-notifications-create-dialog',
    templateUrl: './systems-notifications-create-dialog.component.html',
    styleUrls: ['./systems-notifications-create-dialog.component.scss'],
    standalone: false
})

export class SystemsNotificationsCreateDialogComponent extends AbstractDialogComponent implements OnInit {
    readonly notificationTypesEnum = SystemNotificationsTypeEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly iconTypes = MessageIconTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    subscriptions: Subscription[] = [];
    preferences: PreferencesModel;
    options: SystemNotificationsTypeEnum[] = [
        SystemNotificationsTypeEnum.info,
        SystemNotificationsTypeEnum.important,
        SystemNotificationsTypeEnum.critical,
        SystemNotificationsTypeEnum.maintenance
    ];

    maintenanceTimeRange: DeliveryRangeModel = new DeliveryRangeModel();
    notificationTimeRange: DeliveryRangeModel = new DeliveryRangeModel();

    titleEmpty: boolean = false;

    maintenanceErrors: {[key: string]: boolean} = {
        timeRangeIncomplete: false,
        endDateBeforeStartDate: false
    };
    notificationErrors: {[key: string]: boolean} = {
        timeRangeIncomplete: false,
        endDateBeforeStartDate: false,
        endDateBeforeMaintenanceEndDate: false,
        startDateAfterMaintenanceStartDate: false,
    };

    @Input() notification: SystemNotificationModel = new SystemNotificationModel();

    constructor(
        public coreDialogService: CoreDialogService,
        private currentStateService: CurrentUserStateService,
        private systemNotificationService: SystemNotificationService,
        private messageService: MessageService,
        private localizationService: LocalizationService

    ) {
        super(coreDialogService, 'SystemsNotificationsCreate');
        this.subscriptions.push(
            this.currentStateService.getPreferences().subscribe(
                preferences => {
                    if (preferences) {
                        this.preferences = preferences;
                    }
                }
            )
        );
    }

    ngOnInit(): void {
        if (this.notification.id) {
            this.notificationTimeRange = {
                ongoing: false,
                start: this.notification.notificationTimeRange.start,
                end: this.notification.notificationTimeRange.end,
                timezone: this.notification.notificationTimeRange.timeZone || this.preferences.timeZone
            };

            if (this.notification.maintenanceTimeRange) {
                this.maintenanceTimeRange = {
                    ongoing: false,
                    start: this.notification.maintenanceTimeRange.start,
                    end: this.notification.maintenanceTimeRange.end,
                    timezone: this.notification.maintenanceTimeRange.timeZone || this.preferences.timeZone
                };
            } else {
                this.maintenanceTimeRangeChanged(null);
            }
        } else {
            this.notification.type = SystemNotificationsTypeEnum.info;
            this.notificationTimeRangeChanged(null);
            this.maintenanceTimeRangeChanged(null);
        }
    }

    handleNotificationUpdate(notification: SystemNotificationModel){
        this.systemNotificationService.updateNotification(notification).subscribe();
    }

    handleOptionChange(option) {
        if (option === SystemNotificationsTypeEnum.maintenance) {
            this.notification.title = this.localizationService.transform('SystemNotification.createDialog.title.maintenance');
            this.titleEmpty = false;
            this.checkTimeRangeErrors(true);
        } else {
            if (this.notification.type === SystemNotificationsTypeEnum.maintenance){
                this.notification.title = '';
            }
            this.maintenanceErrors.timeRangeIncomplete = false;
            this.maintenanceErrors.endDateBeforeStartDate = false;
            this.notificationErrors.endDateBeforeMaintenanceEndDate = false;
            this.notificationErrors.startDateAfterMaintenanceStartDate = false;
        }
        this.notification.type = option;
    }

    titleChanged(value: string) {
        this.titleEmpty = !value || value.trim().length === 0;
    }

    notificationTimeRangeChanged(value: DeliveryRangeModel) {
        if (!value) {
            this.notificationTimeRange.ongoing = false;
            this.notificationTimeRange.timezone = this.preferences?.timeZone;
            const now = moment().tz(this.notificationTimeRange.timezone);
            this.notificationTimeRange.start = SystemNotificationModel.dateInTimeZone(now.valueOf(), this.notificationTimeRange.timezone);
            this.notificationTimeRange.end = SystemNotificationModel.dateInTimeZone(now.add(24, 'hours').valueOf(), this.notificationTimeRange.timezone);
        } else {
            this.notificationTimeRange.start = value.start;
            this.notificationTimeRange.end = value.end;
            this.notificationTimeRange.timezone = value.timezone;
        }
        if (this.notification.type === SystemNotificationsTypeEnum.maintenance) {
            this.checkTimeRangeErrors(true);
        } else {
            this.checkTimeRangeErrors(false);
        }
    }

    maintenanceTimeRangeChanged(value: DeliveryRangeModel) {
        if (!value) {
            this.maintenanceTimeRange.ongoing = false;
            this.maintenanceTimeRange.timezone = this.preferences?.timeZone;
            const now = moment().tz(this.maintenanceTimeRange.timezone);
            const start = now.clone().hour(21).minute(0).second(0).millisecond(0);
            const end = now.clone().add(1, 'day').startOf('day');
            this.maintenanceTimeRange.start = SystemNotificationModel.dateInTimeZone(start.valueOf(), this.maintenanceTimeRange.timezone);
            this.maintenanceTimeRange.end = SystemNotificationModel.dateInTimeZone(end.valueOf(), this.maintenanceTimeRange.timezone);
        } else if (this.notification.type === SystemNotificationsTypeEnum.maintenance) {
            this.maintenanceTimeRange.start = value.start;
            this.maintenanceTimeRange.end = value.end;
            this.maintenanceTimeRange.timezone = value.timezone;
            this.checkTimeRangeErrors(true);
        }
    }

    checkTimeRangeErrors(hasMaintenance: boolean) {
        if (hasMaintenance) {
            this.maintenanceErrors.timeRangeIncomplete = !this.maintenanceTimeRange.end;
            this.maintenanceErrors.endDateBeforeStartDate = this.maintenanceTimeRange.end < this.maintenanceTimeRange.start;
            this.notificationErrors.endDateBeforeMaintenanceEndDate = this.notificationTimeRange.end < this.maintenanceTimeRange.end;

            // we want to check if notification start time is within 15 minutes of the maintenance start time, so only need to compare time by minutes
            const diff = (this.notificationTimeRange.start.getTime() - this.maintenanceTimeRange.start.getTime()) / 60000;
            this.notificationErrors.startDateAfterMaintenanceStartDate = Math.floor(diff) > -15;
        }

        this.notificationErrors.timeRangeIncomplete = !this.notificationTimeRange.end;
        this.notificationErrors.endDateBeforeStartDate = this.notificationTimeRange.end < this.notificationTimeRange.start;

    }

    create() {
        this.notification.notificationTimeRange = {
            start: this.notificationTimeRange.start,
            end: this.notificationTimeRange.end,
            timeZone: this.notificationTimeRange.timezone,

            // will be ignored, but I don't want to make these optional in the model
            startConvertedwithTz: this.notification.notificationTimeRange?.startConvertedwithTz || new Date(),
            endConvertedwithTz:  this.notification.notificationTimeRange?.endConvertedwithTz || new Date(),
        };

        if (this.notification.type === SystemNotificationsTypeEnum.maintenance) {
            this.notification.maintenanceTimeRange = {
                start: this.maintenanceTimeRange.start,
                end: this.maintenanceTimeRange.end,
                timeZone: this.maintenanceTimeRange.timezone,

                // will be ignored, but I don't want to make these optional in the model
                startConvertedwithTz: this.notification.maintenanceTimeRange?.startConvertedwithTz || new Date(),
                endConvertedwithTz: this.notification.maintenanceTimeRange?.endConvertedwithTz || new Date(),
            };
        }

        // validate input before creating
        this.titleEmpty = !this.notification.title || this.notification.title.trim().length === 0;
        this.checkTimeRangeErrors(this.notification.type === SystemNotificationsTypeEnum.maintenance);

        if (
            this.titleEmpty
            || this.notificationErrors.timeRangeIncomplete
            || this.maintenanceErrors.timeRangeIncomplete
            || this.notificationErrors.endDateBeforeStartDate
            || this.maintenanceErrors.endDateBeforeStartDate
            || this.notificationErrors.endDateBeforeMaintenanceEndDate
        ) {
            return;
        }

        if (!this.notification.id) {
            this.systemNotificationService.createNewNotification(this.notification).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'SystemNotification.create.success.heading',
                        detail: this.localizationService.transform('SystemNotification.create.success.message', { name: this.notification.title }),
                    });
                    this.systemNotificationService.reloadList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'SystemNotification.create.error.heading',
                        detail: this.localizationService.transform('SystemNotification.create.error.message', { name: this.notification.title }),
                    });
                }
            });
        } else {
            this.systemNotificationService.updateNotification(this.notification).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'SystemNotification.update.success.heading',
                        detail: this.localizationService.transform('SystemNotification.update.success.message', { name: this.notification.title }),
                    });
                    this.systemNotificationService.reloadList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'SystemNotification.update.error.heading',
                        detail: this.localizationService.transform('SystemNotification.update.error.message', { name: this.notification.title }),
                    });

                }
            });
        }
        this.closeDialog();
    }

    cancel() {
        this.closeDialog();
    }
}
